import React, { useEffect, useRef, useState } from "react";
import api, { authFetch } from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { toast } from "sonner";
import { Link } from "react-router-dom";
import { Plus, Edit, UserX, UploadCloud, Download, FileSpreadsheet, CheckCircle2, XCircle, RotateCcw, IdCard, ExternalLink, Search, AlertTriangle } from "lucide-react";

const ROLE_LABEL = {
  admin: "Amministratore", amministrativo: "Amministrativo",
  tecnico: "Tecnico", preposto: "Preposto",
  operator: "Magazziniere", guest: "Ospite (non attivo)",
  visitatore: "Visitatore in cantiere", monitor: "Monitor capannone",
};
const ROLE_COLOR = {
  admin: "bg-slate-800 text-white",
  amministrativo: "bg-indigo-100 text-indigo-700",
  tecnico: "bg-blue-100 text-blue-700",
  preposto: "bg-emerald-100 text-emerald-700",
  operator: "bg-amber-100 text-amber-700",
  guest: "bg-slate-100 text-slate-600",
  monitor: "bg-violet-100 text-violet-700",
};
const ROLES = ["admin", "amministrativo", "tecnico", "preposto", "operator",
               "monitor"];   // l'ospite condiviso non si crea piu'

export default function Utenti() {
  const [list, setList] = useState([]);
  const [showForm, setShowForm] = useState(false);
  const [cerca, setCerca] = useState("");
  const [ruoloFiltro, setRuoloFiltro] = useState("all");
  const [editing, setEditing] = useState(null);
  const [showImport, setShowImport] = useState(false);
  const [employees, setEmployees] = useState([]);

  const load = async () => {
    try {
      const { data } = await api.get("/users");
      setList(data);
    } catch { toast.error("Errore caricamento utenti"); }
  };
  useEffect(() => {
    load();
    api.get("/employees").then(({ data }) => setEmployees(data)).catch(() => {});
  }, []);

  const submit = async (form) => {
    try {
      if (editing) {
        await api.put(`/users/${editing.id}`, form);
        toast.success("Utente aggiornato");
      } else {
        const { data } = await api.post("/users", form);
        toast.success(data.temp_password
          ? `Utente creato · password temporanea: ${data.temp_password}`
          : "Utente creato");
      }
      setShowForm(false); setEditing(null);
      load();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore"); }
  };

  // Con una ventina di account e sette ruoli, scorrere le schede per trovare
  // chi si cerca e' piu' lento che scriverne il nome.
  const visibili = list.filter(u => {
    const testo = cerca.trim().toLowerCase();
    const perRuolo = ruoloFiltro === "all" || u.role === ruoloFiltro;
    if (!testo) return perRuolo;
    return perRuolo && [u.name, u.email, ROLE_LABEL[u.role]]
      .some(v => (v || "").toLowerCase().includes(testo));
  });

  // Chi entra come amministratore, tecnico o amministrativo non va in
  // cantiere: perche' l'app lo sappia, l'account dev'essere collegato alla sua
  // scheda dipendente. Senza il collegamento la persona resta fra i
  // disponibili, e nessuno capisce perche'.
  const scollegati = list.filter(
    (u) => u.active !== false && !u.employee_id
           && ["admin", "tecnico", "amministrativo"].includes(u.role));

  const deactivate = async (u) => {
    if (!window.confirm(`Disattivare l'accesso di ${u.name}?`)) return;
    await api.delete(`/users/${u.id}`);
    toast.success("Utente disattivato");
    load();
  };

  return (
    <div className="space-y-6" data-testid="utenti-page">
      <div className="flex items-end justify-between gap-3 flex-wrap">
        <div>
          <div className="text-xs uppercase tracking-[0.2em] text-slate-500 mb-2">Admin</div>
          <h1 className="font-display text-4xl font-black tracking-tighter">Utenti</h1>
          <p className="text-sm text-slate-500 mt-1">{list.length} account · ruoli e accessi</p>
        </div>
        <div className="flex gap-2 flex-wrap">
          <Button variant="outline" className="h-12" onClick={() => setShowImport(true)} data-testid="import-users-btn">
            <UploadCloud className="w-4 h-4 mr-2" /> Import massivo
          </Button>
          <Button className="bg-orange-500 hover:bg-orange-600 text-white h-12"
                  onClick={() => { setEditing(null); setShowForm(true); }} data-testid="add-user-btn">
            <Plus className="w-4 h-4 mr-2" /> Nuovo utente
          </Button>
        </div>
      </div>

      <div className="relative">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
        <Input placeholder="Cerca per nome, email o ruolo..." className="pl-9 h-11"
               value={cerca} onChange={(e) => setCerca(e.target.value)}
               data-testid="cerca-utente" />
      </div>

      <div className="flex flex-wrap gap-1.5">
        <button type="button" onClick={() => setRuoloFiltro("all")} data-testid="ruolo-all"
                className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${
                  ruoloFiltro === "all" ? "bg-slate-900 border-slate-900 text-white"
                                        : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
          Tutti <span className="opacity-60">{list.length}</span>
        </button>
        {ROLES.map(r => {
          const n = list.filter(u => u.role === r).length;
          if (n === 0) return null;
          return (
            <button key={r} type="button" onClick={() => setRuoloFiltro(r)}
                    data-testid={`ruolo-${r}`}
                    className={`text-sm px-3 py-1.5 rounded-full border transition-colors ${
                      ruoloFiltro === r ? "bg-slate-900 border-slate-900 text-white"
                                        : "border-slate-200 text-slate-600 hover:bg-slate-50"}`}>
              {ROLE_LABEL[r]} <span className="opacity-60">{n}</span>
            </button>
          );
        })}
      </div>

      {/* Un account d'ufficio senza scheda dipendente collegata e' un guaio
          silenzioso: l'app non sa che quella persona in cantiere non ci va, e
          continua a proporla fra i disponibili. Qui si vede e si sistema. */}
      {scollegati.length > 0 && (
        <div className="border border-amber-300 bg-amber-50 rounded-lg p-3 text-sm"
             data-testid="account-scollegati">
          <div className="flex items-start gap-2 text-amber-900">
            <AlertTriangle className="w-4 h-4 mt-0.5 flex-shrink-0" />
            <div className="min-w-0">
              <div className="font-semibold">
                {scollegati.length} account d&apos;ufficio non collegati a una scheda dipendente
              </div>
              <p className="text-xs mt-0.5">
                Finché non sono collegati, queste persone continuano a comparire fra i
                disponibili per i cantieri. Apri l&apos;account, tocca Modifica e scegli il
                dipendente. In alternativa, metti la spunta «Lavora in ufficio» sulla sua scheda.
              </p>
              <div className="flex flex-wrap gap-1.5 mt-2">
                {scollegati.map((u) => (
                  <button key={u.id} type="button"
                          onClick={() => { setEditing(u); setShowForm(true); }}
                          className="text-xs bg-white border border-amber-300 rounded-full px-2.5 py-1
                                     hover:border-amber-500">
                    {u.name} <span className="text-amber-700">· {ROLE_LABEL[u.role] || u.role}</span>
                  </button>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {visibili.length === 0 && (
        <p className="text-sm text-slate-400 text-center py-6">Nessun utente corrisponde.</p>
      )}

      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
        {visibili.map(u => (
          <Card key={u.id} className={`border-slate-200 ${!u.active ? "opacity-50" : ""}`}>
            <CardContent className="p-5">
              <div className="font-display font-bold text-lg leading-tight">{u.name}</div>
              <div className="text-xs text-slate-500 truncate">{u.email}</div>
              <div className="flex items-center gap-2 mt-2 flex-wrap">
                <Badge className={`${ROLE_COLOR[u.role] || ""} border-0`}>{ROLE_LABEL[u.role] || u.role}</Badge>
                {!u.active && <Badge variant="outline" className="text-[10px]">disattivato</Badge>}
              </div>
              {u.employee_id && (
                <Link to={`/fascicolo/${u.employee_id}`} target="_blank"
                      className="text-xs text-orange-600 hover:underline inline-flex items-center gap-1 mt-2">
                  <IdCard className="w-3 h-3" /> Vedi fascicolo dipendente <ExternalLink className="w-3 h-3" />
                </Link>
              )}
              {!u.employee_id && (
                <div className="text-xs text-slate-400 mt-2">Nessun dipendente collegato</div>
              )}
              <div className="flex gap-2 mt-4">
                <Button variant="outline" size="sm" onClick={() => { setEditing(u); setShowForm(true); }}>
                  <Edit className="w-3 h-3 mr-1" /> Modifica
                </Button>
                {u.active && (
                  <Button variant="outline" size="sm" onClick={() => deactivate(u)}
                          className="text-red-600 hover:bg-red-50">
                    <UserX className="w-3 h-3 mr-1" /> Disattiva
                  </Button>
                )}
              </div>
            </CardContent>
          </Card>
        ))}
      </div>

      {showForm && <UserForm initial={editing} employees={employees} onSubmit={submit}
                             onClose={() => { setShowForm(false); setEditing(null); }} />}
      {showImport && <ImportUsersDialog onClose={() => setShowImport(false)} onDone={load} />}
    </div>
  );
}

function UserForm({ initial, employees, onSubmit, onClose }) {
  const [f, setF] = useState(initial
    ? { email: initial.email, name: initial.name, role: initial.role, password: "", active: initial.active, employee_id: initial.employee_id || "" }
    : { email: "", name: "", role: "tecnico", password: "", active: true, employee_id: "" });
  const [empSearch, setEmpSearch] = useState("");
  const submit = (e) => { e.preventDefault(); onSubmit({ ...f, employee_id: f.employee_id || null }); };
  const filteredEmps = (employees || []).filter(e =>
    !empSearch || e.full_name.toLowerCase().includes(empSearch.toLowerCase()) ||
    (e.badge_number || "").toLowerCase().includes(empSearch.toLowerCase()));
  const selectedEmp = (employees || []).find(e => e.id === f.employee_id);
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent>
        <DialogHeader><DialogTitle>{initial ? "Modifica" : "Nuovo"} utente</DialogTitle></DialogHeader>
        <form onSubmit={submit} className="space-y-3">
          <div><Label>Nome e cognome</Label>
            <Input value={f.name} onChange={(e) => setF({ ...f, name: e.target.value })} required /></div>
          <div><Label>Email</Label>
            <Input type="email" value={f.email} disabled={!!initial}
                   onChange={(e) => setF({ ...f, email: e.target.value })} required /></div>
          <div><Label>Ruolo</Label>
            <select className="w-full h-10 border border-slate-200 rounded px-3"
                    value={f.role} onChange={(e) => setF({ ...f, role: e.target.value })}>
              {ROLES.map(r => <option key={r} value={r}>{ROLE_LABEL[r]}</option>)}
            </select>
          </div>
          <div><Label>{initial ? "Nuova password (lascia vuoto per non cambiarla)" : "Password (vuoto = generata automaticamente)"}</Label>
            <Input type="text" value={f.password} onChange={(e) => setF({ ...f, password: e.target.value })} /></div>

          <div>
            <Label>Collega a scheda dipendente (facoltativo)</Label>
            <p className="text-[10px] text-slate-500 mb-1">
              Se questa persona è già in anagrafica Dipendenti, collega l'account: il fascicolo sarà
              consultabile direttamente da qui e dai cantieri a cui verrà assegnata.
            </p>
            {selectedEmp ? (
              <div className="flex items-center justify-between p-2 border border-orange-200 bg-orange-50 rounded">
                <span className="text-sm">{selectedEmp.full_name} · {selectedEmp.badge_number}</span>
                <Button type="button" variant="outline" size="sm" onClick={() => setF({ ...f, employee_id: "" })}>Rimuovi</Button>
              </div>
            ) : (
              <>
                <Input placeholder="Cerca per nome o badge..." className="h-9 text-sm mb-1"
                       value={empSearch} onChange={(e) => setEmpSearch(e.target.value)} />
                {empSearch && (
                  <div className="border border-slate-200 rounded max-h-32 overflow-y-auto">
                    {filteredEmps.slice(0, 8).map(e => (
                      <button key={e.id} type="button"
                              onClick={() => { setF({ ...f, employee_id: e.id }); setEmpSearch(""); }}
                              className="w-full text-left p-2 text-sm hover:bg-slate-50 border-b border-slate-100 last:border-0">
                        {e.full_name} · {e.badge_number}
                      </button>
                    ))}
                    {filteredEmps.length === 0 && <div className="p-2 text-xs text-slate-400">Nessun dipendente trovato</div>}
                  </div>
                )}
              </>
            )}
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Annulla</Button>
            <Button type="submit" className="bg-orange-500 hover:bg-orange-600 text-white">Salva</Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}

function ImportUsersDialog({ onClose, onDone }) {
  const ref = useRef(null);
  const [file, setFile] = useState(null);
  const [busy, setBusy] = useState(false);
  const [report, setReport] = useState(null);

  const downloadTemplate = async () => {
    try {
      const r = await authFetch("/users/import-template");
      if (!r.ok) throw new Error();
      const b = await r.blob();
      const u = window.URL.createObjectURL(b);
      const a = document.createElement("a");
      a.href = u; a.download = "modello_import_utenti_SGM.xlsx"; a.click();
      window.URL.revokeObjectURL(u);
    } catch { toast.error("Errore nel download del modello"); }
  };

  const submit = async () => {
    if (!file) { toast.error("Seleziona il file Excel"); return; }
    setBusy(true);
    const fd = new FormData();
    fd.append("excel_file", file);
    try {
      const { data } = await api.post("/users/bulk-import", fd, { headers: { "Content-Type": "multipart/form-data" } });
      setReport(data);
      if (data.created > 0) toast.success(`${data.created} utenti creati`);
      onDone?.();
    } catch (e) { toast.error(e.response?.data?.detail || "Errore durante l'importazione"); }
    finally { setBusy(false); }
  };

  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle>Import massivo utenti</DialogTitle></DialogHeader>

        {!report && (
          <div className="space-y-4">
            <div className="p-3 bg-slate-50 border border-slate-200 rounded space-y-2">
              <div className="text-sm font-semibold">1 · Scarica e compila il modello</div>
              <p className="text-xs text-slate-600">
                Colonne: email, password, nome_cognome, ruolo. Il cantiere non va indicato qui:
                sarà assegnato dai tecnici direttamente in app quando il cantiere esiste.
              </p>
              <Button variant="outline" size="sm" onClick={downloadTemplate}>
                <Download className="w-3 h-3 mr-1" /> Scarica modello .xlsx
              </Button>
            </div>
            <div>
              <Label>2 · Carica il file compilato</Label>
              <input ref={ref} type="file" accept=".xlsx,.xlsm" hidden
                     onChange={(e) => setFile(e.target.files?.[0] || null)} />
              <Button variant="outline" className="w-full mt-1 justify-start" onClick={() => ref.current?.click()}>
                <FileSpreadsheet className="w-4 h-4 mr-2 flex-shrink-0" />
                <span className="truncate">{file ? file.name : "Seleziona file..."}</span>
              </Button>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={onClose}>Annulla</Button>
              <Button onClick={submit} disabled={busy || !file} className="bg-orange-500 hover:bg-orange-600 text-white">
                <UploadCloud className="w-4 h-4 mr-2" />{busy ? "Importazione..." : "Avvia importazione"}
              </Button>
            </DialogFooter>
          </div>
        )}

        {report && (
          <div className="space-y-4">
            <div className="grid grid-cols-2 gap-2">
              <div className="rounded border border-emerald-200 bg-emerald-50 p-3">
                <div className="text-[10px] uppercase text-emerald-700">Creati</div>
                <div className="font-display text-2xl font-black text-emerald-700">{report.created}</div>
              </div>
              <div className="rounded border border-amber-200 bg-amber-50 p-3">
                <div className="text-[10px] uppercase text-amber-700">Scartati</div>
                <div className="font-display text-2xl font-black text-amber-700">{report.skipped}</div>
              </div>
            </div>
            <div className="border border-slate-200 rounded overflow-x-auto max-h-72 overflow-y-auto">
              <table className="w-full text-xs">
                <thead className="bg-slate-50 text-slate-500 uppercase text-[10px] sticky top-0">
                  <tr><th className="text-left p-2">Email</th><th className="text-left p-2">Esito</th><th className="text-left p-2">Dettaglio</th></tr>
                </thead>
                <tbody>
                  {report.details.map((d, i) => (
                    <tr key={i} className="border-t border-slate-100">
                      <td className="p-2 font-mono truncate max-w-[160px]">{d.email}</td>
                      <td className="p-2">
                        {d.status === "creato"
                          ? <Badge className="bg-emerald-100 text-emerald-700 border-0 text-[10px]"><CheckCircle2 className="w-3 h-3 mr-1" />creato</Badge>
                          : <Badge variant="destructive" className="text-[10px]"><XCircle className="w-3 h-3 mr-1" />scartato</Badge>}
                      </td>
                      <td className="p-2 text-slate-500">{d.reason}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            <DialogFooter>
              <Button variant="outline" onClick={() => setReport(null)}>
                <RotateCcw className="w-4 h-4 mr-2" /> Nuovo import
              </Button>
              <Button onClick={onClose} className="bg-orange-500 hover:bg-orange-600 text-white">Chiudi</Button>
            </DialogFooter>
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
